/**
 * Organizations resource — current org + its widget config (ARCHITECTURE §3, §7).
 */

import type { HttpClient } from '../httpClient';
import { endpoints } from '../endpoints';
import type {
  BotConfig,
  ChatSettings,
  DomainInfo,
  Organization,
  OrgNotificationConfig,
  OrgTheme,
  TranslationConfig,
  TranslationConfigInput,
  WidgetConfig,
} from '../../types';

/** Mutable org-profile fields (Settings page). `slug` is uniqueness-checked server-side. */
export interface UpdateOrgBody {
  name?: string;
  slug?: string;
  theme?: Partial<OrgTheme>;
  /** Chat behavior (timeouts + routing); shallow-merged server-side. */
  chatSettings?: ChatSettings;
}

export class OrganizationsResource {
  constructor(private readonly http: HttpClient) {}

  current(): Promise<Organization> {
    return this.http.get(endpoints.organizations.current());
  }

  /** Patch the current org's profile (name / slug / branding). */
  update(patch: UpdateOrgBody): Promise<Organization> {
    return this.http.patch(endpoints.organizations.current(), patch);
  }

  widgetConfig(): Promise<WidgetConfig> {
    return this.http.get(endpoints.organizations.widgetConfig());
  }

  updateWidgetConfig(patch: Partial<WidgetConfig>): Promise<WidgetConfig> {
    return this.http.patch(endpoints.organizations.widgetConfig(), patch);
  }

  // ── AI bot config (AutoRAG auto-responder, ARCHITECTURE §G) ────────────────
  botConfig(): Promise<BotConfig> {
    return this.http.get(endpoints.organizations.botConfig());
  }

  /** Replace the org's AI bot config (full PUT — the Settings form submits the whole object). */
  updateBotConfig(config: BotConfig): Promise<BotConfig> {
    return this.http.put(endpoints.organizations.botConfig(), config);
  }

  // ── Notifications (org-wide master-gate; parity with old "Notification & Sound") ──
  /** Org-wide notification defaults/gate. Readable by any principal (agents AND it with their own). */
  notificationConfig(): Promise<OrgNotificationConfig> {
    return this.http.get(endpoints.organizations.notificationConfig());
  }

  /** Replace the org's notification master-gate (admin; full PUT of all four channels). */
  updateNotificationConfig(config: OrgNotificationConfig): Promise<OrgNotificationConfig> {
    return this.http.put(endpoints.organizations.notificationConfig(), config);
  }

  // ── Translation provider config (per-org key; admin-only, secret write-only) ──
  /** The org's translation config, MASKED (`apiKeySet` instead of the key). */
  translationConfig(): Promise<TranslationConfig> {
    return this.http.get(endpoints.organizations.translationConfig());
  }

  /** Update the org's translation config (admin). `apiKey` is write-only — omit/blank to keep. */
  updateTranslationConfig(input: TranslationConfigInput): Promise<TranslationConfig> {
    return this.http.put(endpoints.organizations.translationConfig(), input);
  }

  // ── Custom domain (white-label, ARCHITECTURE §11) ──────────────────────────
  getDomain(): Promise<DomainInfo> {
    return this.http.get(endpoints.organizations.domain());
  }

  /** Set/replace the custom domain → status `pending` + the DNS records to publish. */
  setDomain(customDomain: string): Promise<DomainInfo> {
    return this.http.patch(endpoints.organizations.domain(), { customDomain });
  }

  /** Ask the backend to re-check the ownership TXT record → active/failed. */
  verifyDomain(): Promise<DomainInfo> {
    return this.http.post(endpoints.organizations.domainVerify());
  }
}
